import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import './FeaturedTab.css';
import useCatalogManagementStore from '@/stores/AdminStore/CatalogManagementStore';
import { FeaturedCourse, ActiveCourse } from '@/services/PagesServices/CatalogManagementService';
import { MessageModal } from '@/Components/common/MessageModal';
import ConfirmationModal from '@/Components/common/ConfirmationModal/ConfirmationModal';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import RecommendationRuleModal from './RecommendationRuleModal';

const FeaturedTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { featuredCourses, recommendationRules, activeCourses, createFeaturedCourse, toggleFeaturedCourseStatus, fetchActiveCourses, createRecommendationRule, editRecommendationRule, deleteRecommendationRule, fetchRecommendationRules } = useCatalogManagementStore();

  // Limit to 5 items for preview
  const MAX_PREVIEW_ITEMS = 5;
  const displayedFeaturedCourses = featuredCourses?.slice(0, MAX_PREVIEW_ITEMS) || [];
  const displayedRules = recommendationRules?.slice(0, MAX_PREVIEW_ITEMS) || [];

  const [featuredModalOpen, setFeaturedModalOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);

  const [featuredFormData, setFeaturedFormData] = useState({
    courseId: '',
    courseInput: '',
  });

  const [showFeaturedCourseDropdown, setShowFeaturedCourseDropdown] = useState(false);
  const [filteredFeaturedCourses, setFilteredFeaturedCourses] = useState<ActiveCourse[]>([]);

  const [editingRuleData, setEditingRuleData] = useState<{
    rule_name: string;
    rule_condition: string;
    courses: Array<{ id: string; title: string }>;
  } | null>(null);

  const [isOperationLoading, setIsOperationLoading] = useState(false);

  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    ruleId: string | null;
    ruleName: string;
  }>({
    isOpen: false,
    ruleId: null,
    ruleName: '',
  });

  const showMessageModal = (title: string, message: string, type: 'success' | 'error') => {
    setMessageModal({ isOpen: true, title, message, type });
  };

  const closeMessageModal = () => {
    setMessageModal({ ...messageModal, isOpen: false });
  };

  const openFeaturedModal = async () => {
    setFeaturedModalOpen(true);
    setFeaturedFormData({ courseId: '', courseInput: '' });
    // Fetch active courses when modal opens
    try {
      await fetchActiveCourses();
    } catch (error) {
      console.error('Failed to fetch active courses:', error);
    }
  };

  const closeFeaturedModal = () => {
    setFeaturedModalOpen(false);
    setFeaturedFormData({ courseId: '', courseInput: '' });
    setShowFeaturedCourseDropdown(false);
  };

  const handleFeaturedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!featuredFormData.courseId) {
      showMessageModal('Error', 'Please select a course', 'error');
      return;
    }

    // Check if the course is already in the featured courses list
    const isAlreadyFeatured = featuredCourses?.some(
      course => course.id === featuredFormData.courseId
    );

    if (isAlreadyFeatured) {
      const courseName = featuredFormData.courseInput || 'This course';
      showMessageModal('Error', `${courseName} is already added as a featured course.`, 'error');
      return;
    }

    setIsOperationLoading(true);
    try {
      await createFeaturedCourse(parseInt(featuredFormData.courseId));
      closeFeaturedModal();
      showMessageModal('Success', 'Featured course added successfully!', 'success');
    } catch (error: any) {
      console.error('Failed to add featured course:', error);
      showMessageModal('Error', error.message || 'Failed to add featured course. Please try again.', 'error');
    } finally {
      setIsOperationLoading(false);
    }
  };

  const openRuleModal = () => {
    setIsEditMode(false);
    setEditingRuleId(null);
    setEditingRuleData(null);
    setRuleModalOpen(true);
  };

  const openEditRuleModal = (ruleId: string) => {
    const rule = recommendationRules?.find(r => r.id === ruleId);
    if (!rule) return;

    setIsEditMode(true);
    setEditingRuleId(parseInt(ruleId));
    setEditingRuleData({
      rule_name: rule.rule_name,
      rule_condition: rule.rule_condition,
      courses: rule.courses,
    });
    setRuleModalOpen(true);
  };

  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setIsEditMode(false);
    setEditingRuleId(null);
    setEditingRuleData(null);
  };

  const handleRuleModalSuccess = (message: string) => {
    showMessageModal('Success', message, 'success');
  };

  const handleRuleModalError = (message: string) => {
    showMessageModal('Error', message, 'error');
  };

  const openDeleteConfirmation = (ruleId: string, ruleName: string) => {
    setDeleteConfirmation({
      isOpen: true,
      ruleId,
      ruleName,
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      ruleId: null,
      ruleName: '',
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.ruleId) return;

    setIsOperationLoading(true);
    try {
      await deleteRecommendationRule(parseInt(deleteConfirmation.ruleId));
      closeDeleteConfirmation();
      showMessageModal('Success', 'Recommendation rule deleted successfully!', 'success');
    } catch (error: any) {
      console.error('Failed to delete recommendation rule:', error);
      closeDeleteConfirmation();
      showMessageModal('Error', error.message || 'Failed to delete recommendation rule. Please try again.', 'error');
    } finally {
      setIsOperationLoading(false);
    }
  };

  // Filter courses based on search input for featured courses
  useEffect(() => {
    if (activeCourses && featuredFormData.courseInput) {
      const filtered = activeCourses.filter(course =>
        course.title?.toLowerCase().includes(featuredFormData.courseInput.toLowerCase())
      );
      setFilteredFeaturedCourses(filtered);
    } else {
      setFilteredFeaturedCourses(activeCourses || []);
    }
  }, [activeCourses, featuredFormData.courseInput]);

  const handleFeaturedCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFeaturedFormData({ ...featuredFormData, courseInput: value });
    setShowFeaturedCourseDropdown(true);
  };

  const selectFeaturedCourse = (course: ActiveCourse) => {
    setFeaturedFormData({
      courseId: course.id,
      courseInput: course.title,
    });
    setShowFeaturedCourseDropdown(false);
  };

  const handleFeaturedCourseInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowFeaturedCourseDropdown(false);
    }
  };

  const toggleCourseActive = async (courseId: string) => {
    // Find the course to check its current status
    const course = featuredCourses?.find(c => c.id === courseId);
    if (!course) return;

    const wasActive = course.isActive;

    setIsOperationLoading(true);
    try {
      await toggleFeaturedCourseStatus(parseInt(courseId));
      const statusMessage = wasActive
        ? 'Featured course disabled successfully!'
        : 'Featured course enabled successfully!';
      showMessageModal('Success', statusMessage, 'success');
    } catch (error: any) {
      console.error('Failed to toggle featured course status:', error);
      showMessageModal('Error', error.message || 'Failed to update course status', 'error');
    } finally {
      setIsOperationLoading(false);
    }
  };

  // Check if we should open edit modal from navigation state
  useEffect(() => {
    const state = location.state as any;
    if (state?.openEditRuleModal) {
      openEditRuleModal(state.openEditRuleModal);
      // Clear the state so it doesn't trigger again
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <div className="admin-catalog-featured-content">
      <div className="admin-catalog-featured-section">
        <div className="admin-catalog-section-header">
          <div className="admin-catalog-section-title-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="admin-catalog-section-icon">
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="admin-catalog-section-title">Featured Courses</h2>
          </div>
          <button className="admin-catalog-add-featured-button" onClick={openFeaturedModal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Featured Course
          </button>
        </div>

        {!featuredCourses || featuredCourses.length === 0 ? (
          <div className="admin-catalog-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3>No Featured Courses Yet</h3>
            <p>Featured courses will be displayed prominently on the learner catalog homepage. Add your first featured course to get started.</p>
          </div>
        ) : (
          <>
            <div className="admin-catalog-featured-courses-list">
              {displayedFeaturedCourses.map((course) => (
            <div key={course.id} className="admin-catalog-featured-course-item">
              <div className="admin-catalog-drag-handle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M5 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <div className="admin-catalog-course-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className="admin-catalog-course-info">
                <h3 className="admin-catalog-course-title">{course.title}</h3>
                <div className="admin-catalog-course-meta">
                  <span className="admin-catalog-course-category">{course.category}</span>
                  <div className="admin-catalog-course-rating">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span>{course.rating}</span>
                  </div>
                  <span className="admin-catalog-course-enrolled">{course.enrolled} enrolled</span>
                </div>
              </div>

              <div className="admin-catalog-course-actions">
                <label className="admin-catalog-toggle-switch">
                  <input
                    type="checkbox"
                    checked={course.isActive}
                    onChange={() => toggleCourseActive(course.id)}
                  />
                  <span className="admin-catalog-toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
            </div>

            {featuredCourses && featuredCourses.length > 0 && (
              <div className="admin-catalog-view-all-wrapper">
                <button
                  className="admin-catalog-view-all-button"
                  onClick={() => navigate('/admin/catalog/featured-courses')}
                >
                  View All Featured Courses ({featuredCourses.length})
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="admin-catalog-recommendation-section">
        <div className="admin-catalog-section-header">
          <div className="admin-catalog-section-title-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="admin-catalog-section-icon">
              <path
                d="M22 12h-4l-3 9L9 3l-3 9H2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="admin-catalog-section-title">Recommendation Rules</h2>
          </div>
          <button className="admin-catalog-add-rule-button" onClick={openRuleModal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Rule
          </button>
        </div>

        {!recommendationRules || recommendationRules.length === 0 ? (
          <div className="admin-catalog-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                d="M22 12h-4l-3 9L9 3l-3 9H2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3>No Recommendation Rules Yet</h3>
            <p>Create smart recommendation rules to automatically suggest relevant courses to learners based on their role, department, tenure, or completion history.</p>
          </div>
        ) : (
          <>
            <div className="admin-catalog-rules-list">
              {displayedRules.map((rule) => (
            <div key={rule.id} className="admin-catalog-rule-item">
              <div className="admin-catalog-rule-header">
                <div className="admin-catalog-rule-title-row">
                  <h3 className="admin-catalog-rule-name">{rule.rule_name}</h3>
                  <span className={`admin-catalog-status-badge admin-catalog-${rule.status?.toLowerCase() || 'inactive'}`}>
                    {rule.status || 'Inactive'}
                  </span>
                </div>
                <div className="admin-catalog-rule-actions">
                  <button
                    className="admin-catalog-action-button"
                    aria-label="Edit"
                    onClick={() => openEditRuleModal(rule.id)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="admin-catalog-action-button"
                    aria-label="Delete"
                    onClick={() => openDeleteConfirmation(rule.id, rule.rule_name)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="admin-catalog-rule-description">{rule.rule_condition}</p>
              <div className="admin-catalog-rule-tags">
                {rule.courses.map((course, index) => (
                  <span key={index} className="admin-catalog-rule-tag">
                    {course.title}
                  </span>
                ))}
              </div>
            </div>
          ))}
            </div>

            {recommendationRules && recommendationRules.length > 0 && (
              <div className="admin-catalog-view-all-wrapper">
                <button
                  className="admin-catalog-view-all-button"
                  onClick={() => navigate('/admin/catalog/recommendation-rules')}
                >
                  View All Rules ({recommendationRules.length})
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Featured Course Modal */}
      {featuredModalOpen && ReactDOM.createPortal(
        <div className="admin-catalog-modal-overlay" onClick={closeFeaturedModal}>
          <div className="admin-catalog-modal-content admin-catalog-featured-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-catalog-modal-header">
              <div className="admin-catalog-modal-header-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="admin-catalog-modal-title">Add Featured Course</h2>
              <button className="admin-catalog-modal-close" onClick={closeFeaturedModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleFeaturedSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="featuredCourse" className="admin-catalog-form-label">
                  Select Course <span className="admin-catalog-required">*</span>
                </label>
                <div className="admin-catalog-course-input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="featuredCourse"
                    className="admin-catalog-form-input"
                    placeholder="Search for courses..."
                    value={featuredFormData.courseInput}
                    onChange={handleFeaturedCourseInputChange}
                    onKeyDown={handleFeaturedCourseInputKeyPress}
                    onFocus={() => setShowFeaturedCourseDropdown(true)}
                    required
                  />
                  <button
                    type="button"
                    className="admin-catalog-dropdown-toggle-btn"
                    onClick={() => setShowFeaturedCourseDropdown(!showFeaturedCourseDropdown)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  {showFeaturedCourseDropdown && filteredFeaturedCourses.length > 0 && (
                    <div
                      className="admin-catalog-course-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {filteredFeaturedCourses.map((course) => (
                        <div
                          key={course.id}
                          className="admin-catalog-course-dropdown-item"
                          onClick={() => selectFeaturedCourse(course)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: 500, fontSize: '14px' }}>
                            {course.title}
                          </div>
                          {course.description && (
                            <div style={{ fontSize: '12px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {course.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-catalog-info-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8h.01M12 12v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <div>
                  <strong>Featured Course Settings</strong>
                  <p>Featured courses will be displayed prominently on the learner catalog homepage. Use the order controls to arrange them.</p>
                </div>
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeFeaturedModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add Course
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Recommendation Rule Modal */}
      <RecommendationRuleModal
        isOpen={ruleModalOpen}
        onClose={closeRuleModal}
        onSuccess={handleRuleModalSuccess}
        onError={handleRuleModalError}
        editRuleId={editingRuleId}
        editRuleData={editingRuleData}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && ReactDOM.createPortal(
        <ConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          onClose={closeDeleteConfirmation}
          onConfirm={handleDeleteConfirm}
          title="Delete Recommendation Rule"
          message={`Are you sure you want to delete "${deleteConfirmation.ruleName}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
          isLoading={isOperationLoading}
        />,
        document.body
      )}

      {/* Message Modal for Success/Error */}
      {messageModal.isOpen && ReactDOM.createPortal(
        <MessageModal
          isOpen={messageModal.isOpen}
          onClose={closeMessageModal}
          type={messageModal.type}
          title={messageModal.title}
          message={messageModal.message}
        />,
        document.body
      )}

      {/* Full Screen Loading Spinner */}
      {isOperationLoading && ReactDOM.createPortal(
        <LoadingSpinner fullScreen message="Processing..." />,
        document.body
      )}
    </div>
  );
};

export default FeaturedTab;
